import React, { useMemo, useState } from "react";
import { Typography, Box, IconButton, Tooltip, Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import type { SectorAdminRow } from "../../types";
import { UseTheme } from "lib/hooks";
import * as table from "lib/table";
import { BaseTable } from "components";
import { EditSectorAdminDialog } from "../Dialogs/EditSectorAdminDialog";
import { TableHeaderStyles } from "lib/styles/table";
import { GradientHeaderText } from "lib/styles/header";

export interface SectorAdminsTableProps {
	rows: SectorAdminRow[];
	order: import("enums/sort").SortEnum;
	onRequestSort: (key: keyof SectorAdminRow) => void;
	loading?: boolean;
	page?: number; // Page number (0-indexed)
	rowsPerPage?: number; // Number of rows per page
	title?: string; // Table title
	onDeleteSuccess?: () => void; // Callback after successful edit (refresh table)
	onAddClick?: () => void; // Callback when Add button is clicked
}

/**
 * Table component for displaying Sector Admins.
 *
 * Displays one row per Sector Admin user, showing all sectors they're admin for.
 * Edit opens a dialog with SectorDropdown; removing sectors shows a blue confirmation checkbox.
 */
export function SectorAdminsTable({
	rows,
	order,
	onRequestSort,
	loading = false,
	page = 0,
	rowsPerPage = 25,
	title = "Sector Admins",
	onDeleteSuccess,
	onAddClick,
}: SectorAdminsTableProps) {
	const { theme } = UseTheme();
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [selectedRow, setSelectedRow] = useState<SectorAdminRow | null>(null);

	const headCells = useMemo(
		() =>
			table.CreateHeadCells<
				SectorAdminRow,
				unknown,
				keyof SectorAdminRow,
				string
			>(
				[
					{ id: "name", numeric: false, label: "Name" },
					{
						id: "email",
						numeric: false,
						label: "Email",
						mapper: (o) => o || "",
					},
					{
						id: "sectors",
						numeric: false,
						label: "Sector",
						mapper: (o) =>
							Array.isArray(o)
								? [...o].sort((a, b) => a.localeCompare(b)).join(", ")
								: "",
					},
				],
				(property) => {
					const fieldMap: Record<string, keyof SectorAdminRow> = {
						name: "name",
						email: "email",
						sectors: "sectors",
					};
					const field = fieldMap[property as string];
					if (field) {
						onRequestSort(field);
					}
				}
			),
		[onRequestSort]
	);

	const handleEditClick = (row: SectorAdminRow) => {
		setSelectedRow(row);
		setEditDialogOpen(true);
	};

	const handleCloseEditDialog = () => {
		setEditDialogOpen(false);
		setSelectedRow(null);
	};

	return (
		<>
			<BaseTable<SectorAdminRow, keyof SectorAdminRow>
				data={rows}
				headCells={headCells}
				order={order}
				page={page}
				rowsPerPage={rowsPerPage}
				config={{
					stickyHeader: true,
					header: {
						title: (
							<Typography
								variant="h5"
								sx={GradientHeaderText(theme, false)}
							>
								{title}
							</Typography>
						),
						actions: [
							<Button
								key="add-sector-admin"
								variant="text"
								color="primary"
								size="small"
								sx={{ textTransform: "none" }}
								onClick={onAddClick}
								disabled={loading || !onAddClick}
							>
								+ Add Sector Admin
							</Button>,
						],
					},
				}}
				loading={loading}
				hideActions={false}
				renderActions={(row) => (
					<Box
						sx={{
							display: "flex",
							justifyContent: "flex-end",
							gap: 0.5,
						}}
					>
						<Tooltip title="Edit Sector Admin">
							<IconButton
								onClick={(e) => {
									e.stopPropagation();
									handleEditClick(row);
								}}
								color="primary"
								size="small"
							>
								<EditIcon />
							</IconButton>
						</Tooltip>
					</Box>
				)}
				getRowId={(row) => row.userId}
				sx={TableHeaderStyles(theme)}
			/>

			<EditSectorAdminDialog
				open={editDialogOpen}
				onClose={handleCloseEditDialog}
				onSuccess={() => {
					handleCloseEditDialog();
					onDeleteSuccess?.();
				}}
				selectedRow={selectedRow}
			/>
		</>
	);
}
